import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiAccount, mdiArrowLeft, mdiClose } from "@mdi/js";
import { useStripe, useElements, Elements, PaymentElement } from "@stripe/react-stripe-js";
import { loadStripe } from '@stripe/stripe-js';

import logo from "./logo.png";
import stripeLogo from "./stripe.svg";
import './App.scss';

const loadBlack = "https://static.wixstatic.com/media/84c534_e5bc8f7e29b54e00a6fc2e788719b9de~mv2.gif",
  loadWhite = "https://static.wixstatic.com/media/84c534_04c86af4213e450abcacbd396a83a24e~mv2.gif";

function Item({item: {displayName, img, description}}) {
  return (
    <div className="item">
      <img src={img} alt={displayName}/>
      <div className="item-info">
        <span className="item-name">{displayName}</span>
        <span className="item-price">{description}</span>
      </div>
    </div>
  );
}


function App() {
  const [loading, setLoading] = useState(true);
  const [loadCheckout, setLoadCheckout] = useState(false);
  const [sessionInfo, setSessionInfo] = useState();
  const [bannerHide, setBannerHide] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [loadPromoCode, setLoadPromoCode] = useState(false);
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [dialog, setDialog] = useState();
  const [dialogOpen, setDialogOpen] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const getCookie = (name) => {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return match[2];
  };

  const displayPrice = (price) => {
    const rounded = Math.floor(price * 100) / 100;
    return rounded === 0 ? `FREE` : `${Number(rounded).toLocaleString(undefined, {currency: "USD", style: "currency"})}`;
  };

  const handleError = (json, throwUnknownError = true) => {
    if (json?.message === "%LOGGEDOUT%") {
      setDialog({
        title: "Session expired",
        message: "Your login session has expired due to inactivity. Please go back and try again.",
        buttons: [{label: "GO BACK", onClick: () => window.location.href = "https://dancecuts.com/premium"}],
      });
      setDialogOpen(true);
      return true;
    }
    if (throwUnknownError) {
      setDialog({
        title: "Failed to load checkout",
        message: `${json?.errorMessage || "Something went wrong loading your checkout details."} Please go back and try again, and contact support if the issue keeps occuring.`,
        buttons: [{label: "GO BACK", onClick: () => window.location.href = "https://dancecuts.com/premium"}],
      });
      setDialogOpen(true);
      return true;
    }
  };

  useEffect(() => {
    //initial load call
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const id = params.get("id");
        const json = await (await fetch(`https://api.dancecuts.com/${params.get("dev") ? "v2/dev" : "v2/prod"}/checkout?id=${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${params.get("at")}`,
          },
        })).json();
        if (!json || !json.success) {
          return handleError(json);
        }
        const seshInfo = {
          ...json,
          items: json?.items && JSON.parse(json.items)?.map(item => ({
            ...item,
            displayName: item.name,
            description: item.description || `${displayPrice(item.price)}/${item.period}`,
            finalPrice: item.price,
          })),
        };
        setSessionInfo(seshInfo);       
        if (json?.promoCode && json?.promoCodeDetails) {
          setPromoCode(json.promoCode);
          applyPromoCode(json.promoCodeDetails, seshInfo, json.promoCode);
        }
        setLoading(false);
        window.ga_userdata = { // this variable is obtained from Google Tag Manager to update conversion info
          email: json?.email,
          address: {
            first_name: json?.givenName?.split(" ")[0],
            last_name: json?.givenName?.split(" ").length > 1 ? json?.givenName?.split(' ').splice(1).join(" ") : null,
            city: json?.city,
            region: json?.state,
            country: json?.country,
            postal_code: json?.zip,
          },
        };  

        await fetch(`https://api.dancecuts.com/${params.get("dev") ? "v2/dev" : "v2/prod"}/checkout`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${params.get("at")}`,
          },
          body: JSON.stringify({
            id,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
          }),
        });
      }
      catch (err) {
        console.log(`ERROR: `, err);
        handleError();
      }
    })();
  }, []);

  useEffect(() => {
    window.subscription_amount = sessionInfo?.items?.reduce((acc, val) => acc + val?.finalPrice || val?.price || 9.99, 0); // this variable is obtained from Google Tag Manager to update conversion info
  });


  const applyPromoCode = async (details, sessionInfoOverride, promoCodeOverride) => {
    if (!details) {
      setLoadPromoCode(true);
      setErrorMessage();
      if (!promoCode) {
        setLoadPromoCode(false);
        return true;
      }
      const {discounted} = sessionInfo;
      const params = new URLSearchParams(window.location.search);   
      const id = params.get("id");
      if (discounted === true || discounted === "true") {
        setErrorMessage("Oops! Looks like you already have a discount on your Premium Membership!\nPromo codes cannot be used. Reach out to our support team with any questions!");
        setLoadPromoCode(false);
        return false;
      }
      const response = await (await fetch(`https://api.dancecuts.com/${params.get("dev") ? "v2/dev" : "v2/prod"}/checkout`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${params.get("at")}`,
        },
        body: JSON.stringify({
          id,
          promoCode,
        }),
      }))?.json();
      if (response?.errorMessage || !response?.success) {
        if (handleError(response, false)) {
          return false;
        }
        setErrorMessage(response.errorMessage || "Promo code is invalid.");
        setLoadPromoCode(false);
        return false;
      }
      details = response;
    }
    
    const {discount, isFixed, duration, message, isReferralCode} = details;
    const {items} = sessionInfo || sessionInfoOverride;
    setSessionInfo({
      ...sessionInfo,
      promoMessage: message,
      items: items.map(item => {
        const newPrice = isFixed ? item.price - discount : item.price * (1 - discount);
        return ({
          ...item,
          displayName: `${item.name} - ${isReferralCode ? "Referral" : promoCode || promoCodeOverride}`,
          description: `${displayPrice(newPrice)}\
  ${(item.period !== "year" || duration === -1) && duration !== 1 && newPrice > 0 ? `/${item.period}` : ""}\
  ${item.period !== "year"
    ? duration !== -1 ? ` for ${duration === 1 ? "the first month" : `${duration} months`}, then ${displayPrice(item.price)}/${item.period}` : ""
    : duration !== -1 ? ` for ${Math.ceil(duration / 12) === 1 ? "the first year" : `${Math.ceil(duration / 12)} years`}, then ${displayPrice(item.price)}/${item.period}` : ""}`,
          finalPrice: newPrice,
        })
      })
    });
    setLoadPromoCode(false);
    setPromoCodeApplied(true);
    return true;
  };

  const handleSubmit = async (ignorePromoCode = false) => {
    if (!stripe || !elements)
      return;

    if (ignorePromoCode !== true && promoCode && !promoCodeApplied) {
      setDialog({
        title: "Apply promo code?",
        message: "It looks like you entered a promo code but didn't apply it yet. Do you want to apply it before submitting your payment?",
        buttons: [{
          label: "YES, APPLY",
          onClick: async () => {
            setDialogOpen(false);
            if (await applyPromoCode()) {
              handleSubmit(true);
            }
          },
        }, {
          label: "NO, CONTINUE",
          onClick: () => {
            setDialogOpen(false);
            handleSubmit(true);
          },
        }, {
          label: "CANCEL",
          onClick: "close",
        }],
      });
      return setDialogOpen(true);
    }

    if (!new URLSearchParams(window.location.search).get("dev")) {
      window.fbq('track', 'Purchase', {currency: "USD", value: window.subscription_amount});
      window.gtag("event", "conversion", {
        send_to: "AW-328697338/F4BKCOiUqMMDEPqL3pwB",
        value: window.subscription_amount,
        currency: "USD",
        transaction_id: "",
      });
    }

    setLoadCheckout(true);
    setErrorMessage();
    const {redirectUrl} = sessionInfo;

    const {error} = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: redirectUrl || 'https://dancecuts.com/checkout-success',
      },
    });

    if (error) {
      setErrorMessage(error.message);
    }
    setLoadCheckout(false);
  };

  return (<div className={`${sessionInfo?.banner_text ? "app-inline-items" : "app"}`}>
    <header className="header">
      <a href={sessionInfo?.backUrl || "https://dancecuts.com/premium"} className="header-left">
        <Icon path={mdiArrowLeft} size={1}/>
        <span className="header-back-title">Back</span>
      </a>
      <a href="https://dancecuts.com/member-home" className="header-center">
        <img src={logo} alt="Dance Cuts logo"/>
      </a>
    </header>
    <div className="mobile-container">
      {sessionInfo?.banner_text && <div className="banner-container">
        <div className={`banner${bannerHide ? " banner-hide" : ""}`}>
          <span>{sessionInfo.banner_text.split('\n').map(txt => <p key={txt}>{txt}</p>)}</span>
          <Icon path={mdiClose} size={1} onClick={() => setBannerHide(true)}/>
        </div>
      </div>}
      <div className="form-container">
        {loading ? <>
          <img src={loadBlack} alt="Loading..." className="load-spinner"/>
        </> : <>
          <h1>Checkout</h1>
          <div className="account">
            <Icon path={mdiAccount} size={1}/>
            <div className="account-info">
              <div>Signed in as <span className="account-email">{sessionInfo?.email}</span>.</div>
              <a href={`https://dancecuts.com/login?action=checkout&url=${sessionInfo?.backUrl?.replace("https://dancecuts.com") || "/premium"}`} className="account-sign-out">Not you?</a>
            </div>
          </div>
          <div className="items">
            <span className="items-title">Your cart</span>
            {sessionInfo?.items?.length > 0 && sessionInfo.items.map((item, i) =>
              <Item item={item} key={i}/>
            )}
          </div>
          <PaymentElement/>
          {promoCodeApplied && sessionInfo?.promoMessage && <div className="promoMessage">
            {sessionInfo.promoMessage}
          </div>}
          <div className="promoCode">
            <span>Promo/Referral code:</span>
            <input 
              placeholder="EXAMPLE20" 
              disabled={loadPromoCode}
              value={promoCode} 
              onChange={(e) => {
                setPromoCodeApplied(false);
                setErrorMessage();
                setSessionInfo({
                  ...sessionInfo,
                  items: sessionInfo?.items?.map(item => ({
                    ...item,
                    displayName: item.name,
                    description: item.description || `${displayPrice(item.price)}/${item.period}`,
                    finalPrice: item.price,
                  })),
                });
                setPromoCode(e.target.value)}}
            />
            <button className={promoCodeApplied ? "success" : ""} onClick={() => applyPromoCode()} disabled={loadPromoCode || promoCodeApplied}>
              {loadPromoCode && <img src={loadWhite} alt="Loading spinner"/>}
              {promoCodeApplied && <span className="material-symbols-outlined">
                check
              </span>}
              {promoCodeApplied ? "APPLIED" : "APPLY"}
            </button>
          </div>
          <div className="securedBox">
            <div className="secured">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center"}}>
                <span className="material-symbols-outlined">
                  lock
                </span>
                <span>&nbsp;Your purchase is <b>secured.</b></span>
              </div>
            </div>
            <a href="https://stripe.com" target="_blank" rel="noreferrer"><img className="stripeLogo" src="https://images.ctfassets.net/fzn2n1nzq965/4M6d6BSWzlgsrJx8rdZb0I/733f37ef69b5ca1d3d33e127184f4ce4/Powered_by_Stripe.svg?q=80&w=1082" alt="Powered by Stripe"/></a>
          </div>
          {errorMessage && <div className="error">
            {errorMessage.split("\n").map(i => <p key={i}>{i}</p>)}
          </div>}
          <button
            className={`btn-purchase${loadCheckout ? " btn-purchase-disabled" : ""}`} 
            disabled={loadCheckout}
            onClick={handleSubmit}
            id="btn-purchase"
          >
            {loadCheckout && <img src={loadWhite} className="checkout-loader" alt="Loading spinner"/>}
            SUBMIT PAYMENT
          </button>
          {sessionInfo?.submit_note && <p className="submit-note">{sessionInfo.submit_note}</p>}
        </>}
      </div>
    </div>
    <div className={dialogOpen ? "dialogVisible" : "dialogHidden"}>
      <div className="modal" onClick={() => setDialogOpen(false)}/>
      <div className="dialogContainer">
        <div className="dialog">
          <h1>{dialog?.title}</h1>
          <div className="msg">{dialog?.message}</div>
          <div className="btns">
            {dialog?.buttons.map(({label, onClick}) => <button key={label} onClick={onClick === "close" ? () => setDialogOpen(false) : onClick}>{label}</button>)}
          </div>
        </div>
      </div>
    </div>
  </div>);
}

const stripe = loadStripe(new URLSearchParams(window.location.search).get("dev") ? "pk_test_GetjrLAn82NhpWve5QcxiZqD004BhwRQBz" : "pk_live_V8s7b4oiAKWEp6OJAwhI2uBd00By1nGOJJ");
export default function Wrapper() {
  const secret = new URLSearchParams(window.location.search).get("sid");
  return (
    <Elements stripe={stripe} options={{
      clientSecret: secret,
      fonts: [{
        cssSrc: "https://fonts.googleapis.com/css?family=Barlow"
      }],
    }}>
      <App/>
    </Elements>
  );
}
